/**
 * vec2.ts — Minimal 2D vector on the ground plane.
 *
 * Replaces `Phaser.Math.Vector2` now that the renderer is Three.js. Gameplay stays
 * in 2D `(x, y)`; only the render layer lifts it into 3D (see render/). Mutating
 * helpers exist so hot loops can reuse scratch vectors and allocate nothing.
 */

export interface Vec2 {
  x: number;
  y: number;
}

export function vec2(x = 0, y = 0): Vec2 {
  return { x, y };
}

export function set(out: Vec2, x: number, y: number): Vec2 {
  out.x = x;
  out.y = y;
  return out;
}

export function copy(out: Vec2, from: Vec2): Vec2 {
  out.x = from.x;
  out.y = from.y;
  return out;
}

export function length(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

export function lengthSq(v: Vec2): number {
  return v.x * v.x + v.y * v.y;
}

/** Scale to unit length in place. A zero vector is left untouched. */
export function normalize(out: Vec2): Vec2 {
  const len = Math.hypot(out.x, out.y);
  if (len > 0) {
    out.x /= len;
    out.y /= len;
  }
  return out;
}

export function scale(out: Vec2, k: number): Vec2 {
  out.x *= k;
  out.y *= k;
  return out;
}

/** Rescale to exactly `max` length if currently longer. */
export function clampLength(out: Vec2, max: number): Vec2 {
  const len = Math.hypot(out.x, out.y);
  if (len > max && len > 0) {
    out.x = (out.x / len) * max;
    out.y = (out.y / len) * max;
  }
  return out;
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function distanceSq(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}
